import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthProvider, RequireAuth, useAuth } from '@milliswift/auth'
import { DbProvider, useDb } from './lib/db'
import {
  APP_URLS,
  consoleQueries,
  createQueryClient,
  installMocks,
  queryKeys,
  useWebSocket,
  type PushFrame,
} from '@milliswift/api-client'
import {
  AppShell,
  DropdownMenu,
  ErrorBoundary,
  PageSkeleton,
  ToastProvider,
} from '@milliswift/ui'
import {
  BarChart3,
  ChevronDown,
  Database,
  GitCommit,
  LayoutDashboard,
  Settings,
  Table2,
  Terminal,
} from 'lucide-react'
import { Navigate, Route, Routes } from 'react-router-dom'

import Dashboard from './pages/Dashboard'
import QueryPage from './pages/Query'
import SettingsPage from './pages/Settings'
import Snapshots from './pages/Snapshots'
import Tables from './pages/Tables'
import TableDetail from './pages/TableDetail'
import Usage from './pages/Usage'

const queryClient = createQueryClient()
installMocks()

const NAV = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, matchPrefix: false },
  { label: 'Query', href: '/query', icon: Terminal, matchPrefix: false },
  { label: 'Tables', href: '/tables', icon: Table2, matchPrefix: true },
  { label: 'Snapshots', href: '/snapshots', icon: GitCommit, matchPrefix: false },
  { label: 'Usage', href: '/usage', icon: BarChart3, matchPrefix: false },
  { label: 'Settings', href: '/settings', icon: Settings, matchPrefix: false },
]

/** Header database selector. */
function DatabaseSelector() {
  const { databaseId, setDatabaseId } = useDb()
  const databases = useQuery(consoleQueries.databases())
  const list = databases.data ?? []
  const active = list.find((db) => db.database_id === databaseId) ?? list[0]

  return (
    <DropdownMenu
      align="start"
      trigger={
        <button
          type="button"
          className="inline-flex h-9 items-center gap-2 rounded-control border border-line bg-surface px-3 text-sm font-medium text-foreground transition-colors hover:bg-sunken"
        >
          <Database className="h-4 w-4 text-subtle" aria-hidden />
          <span className="max-w-40 truncate">{active?.name ?? 'Select database'}</span>
          <ChevronDown className="h-3.5 w-3.5 text-subtle" aria-hidden />
        </button>
      }
      items={list.map((db) => ({
        id: db.database_id,
        label: db.name,
        checked: db.database_id === active?.database_id,
        onSelect: () => setDatabaseId(db.database_id),
      }))}
    />
  )
}

function Shell() {
  const { logout } = useAuth()
  const queryClient = useQueryClient()

  // Push channel: invalidate the console queries on relevant events. The
  // queries' own staleness handles the degraded/polling case.
  useWebSocket('/ws', {
    onMessage: (frame: PushFrame) => {
      if (frame.event.includes('device')) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.devices })
      }
    },
  })

  return (
    <DbProvider>
      <AppShell
        variant="full"
        nav={NAV}
        accountUrl={APP_URLS.myaccount}
        headerSlot={<DatabaseSelector />}
        onSignOut={async () => {
          await logout()
          window.location.href = '/login'
        }}
      >
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/query" element={<QueryPage />} />
          <Route path="/tables" element={<Tables />} />
          <Route path="/tables/:name" element={<TableDetail />} />
          <Route path="/snapshots" element={<Snapshots />} />
          <Route path="/usage" element={<Usage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AppShell>
    </DbProvider>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider redirectOnUnauthorized={false}>
            <RequireAuth loading={<PageSkeleton />}>
              <Shell />
            </RequireAuth>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
